// The admin's building blocks. The shell fills the window: a slim sidebar and a page that never scrolls as a
// whole on desktop. Long lists scroll inside their card and page 50 rows at a time; an account or an order
// opens in a sheet over its list instead of a separate page.
import { useEffect, useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Activity, ChevronLeft, ChevronRight, ExternalLink, LayoutDashboard, LogOut, Receipt, Settings2, Users, X } from 'lucide-react';
import { MARK_SVG } from '@mathladder/shared';
import { ThemeToggle } from '@mathladder/shared/react';
import { api } from './api';
import { useAdmin, useTheme } from './lib';

export { Select } from '@mathladder/shared/react';

// ---------------------------------------------------------------- small pieces
export function Field({ label, hint, ...rest }: { label: string; hint?: ReactNode } & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input id={id} className="input" {...rest} />
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

export function Alert({ kind = 'bad', children }: { kind?: 'good' | 'warn' | 'bad' | 'info'; children: ReactNode }) {
  if (!children) return null;
  return <div className={`alert ${kind === 'info' ? '' : `alert-${kind}`}`} role={kind === 'bad' ? 'alert' : 'status'}>{children}</div>;
}

export function Loading({ inline }: { inline?: boolean }) {
  return <div className={`muted loading${inline ? ' inline' : ''}`} role="status"><span className="spinner" aria-hidden="true" /> Loading…</div>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Button({ busy, children, className = 'btn', ...rest }: { busy?: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button className={className} disabled={busy || rest.disabled} aria-busy={busy || undefined} {...rest}>
      {busy && <span className="spinner" aria-hidden="true" />}
      {children}
    </button>
  );
}

/** Collapsible JSON for log detail columns. */
export function Json({ value }: { value: unknown }) {
  if (value == null || (typeof value === 'object' && !Object.keys(value as object).length)) return null;
  return (
    <details className="json-d">
      <summary>detail</summary>
      <pre className="json">{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
}

/** Search box that searches as you type (after a short pause). */
export function Search({ value, onSearch, placeholder }: { value: string; onSearch: (q: string) => void; placeholder: string }) {
  const [q, setQ] = useState(value);
  useEffect(() => {
    if (q.trim() === value) return;
    const t = setTimeout(() => onSearch(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q, value, onSearch]);
  return <input className="input input-sm search" type="search" aria-label="Search" placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} />;
}

// ---------------------------------------------------------------- shell
const NAV = [
  { to: '/', label: 'Overview', Icon: LayoutDashboard, end: true },
  { to: '/customers', label: 'Customers', Icon: Users },
  { to: '/sales', label: 'Sales', Icon: Receipt },
  { to: '/activity', label: 'Activity', Icon: Activity },
  { to: '/system', label: 'System', Icon: Settings2 },
];

export function Shell() {
  const { admin, refresh } = useAdmin();
  const [theme, setTheme] = useTheme();
  const signOut = async () => { await api.post('/admin/logout').catch(() => {}); await refresh(); };
  const site = location.hostname === 'localhost' ? 'http://localhost:4321' : 'https://mathladder.net';
  return (
    <div className="shell">
      <a href="#main" className="skip-link">Skip to content</a>
      <nav className="side" aria-label="Main">
        <NavLink to="/" className="side-mark" aria-label="Admin home">
          <span dangerouslySetInnerHTML={{ __html: MARK_SVG }} style={{ display: 'contents' }} />
          <span className="side-label">Math Ladder</span>
        </NavLink>
        <ul>
          {NAV.map(({ to, label, Icon, end }) => (
            <li key={to}>
              <NavLink to={to} end={end} title={label}><Icon size={18} strokeWidth={1.9} aria-hidden="true" /><span className="side-label">{label}</span></NavLink>
            </li>
          ))}
        </ul>
        <div className="side-foot">
          <a href={site} target="_blank" rel="noopener" title="Open mathladder.net"><ExternalLink size={17} aria-hidden="true" /><span className="side-label">mathladder.net</span></a>
          <button type="button" onClick={() => void signOut()} title="Sign out"><LogOut size={17} aria-hidden="true" /><span className="side-label">Sign out</span></button>
          <div className="side-who side-label">
            <span className="truncate">{admin?.email}</span>
            <span className="mono muted">{__ML_VERSION__}</span>
          </div>
          <div className="side-theme"><ThemeToggle value={theme} onChange={setTheme} /></div>
        </div>
      </nav>
      <main id="main" className="main">
        <Outlet />
      </main>
    </div>
  );
}

/** A page: header, optional tabs, then content that fills the rest of the window. */
export function Page({ title, sub, actions, tabs, children }: { title: ReactNode; sub?: ReactNode; actions?: ReactNode; tabs?: { to: string; label: string; count?: number | null }[]; children: ReactNode }) {
  return (
    <div className="page">
      <header className="page-head">
        <div className="page-title">
          <h1>{title}</h1>
          {sub && <p>{sub}</p>}
        </div>
        {actions && <div className="page-actions">{actions}</div>}
      </header>
      {tabs && (
        <nav className="tabs" aria-label="Sections">
          {tabs.map((t) => <NavLink key={t.to} to={t.to}>{t.label}{t.count ? <span className="tab-count">{t.count}</span> : null}</NavLink>)}
        </nav>
      )}
      <div className="page-body">{children}</div>
    </div>
  );
}

export function Card({ title, sub, actions, children, className = '', flush }: { title?: ReactNode; sub?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; flush?: boolean }) {
  return (
    <section className={`acard ${className}`}>
      {(title || actions) && (
        <header className="acard-head">
          <div className="min0">
            {title && <h2>{title}</h2>}
            {sub && <p>{sub}</p>}
          </div>
          {actions && <div className="acard-actions">{actions}</div>}
        </header>
      )}
      <div className={`acard-body${flush ? ' flush' : ''}`}>{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------- figures
export function Sparkline({ values }: { values: number[] }) {
  if (values.length < 2) return null;
  const w = 96, h = 28, max = Math.max(...values), min = Math.min(...values);
  const x = (i: number) => (i / (values.length - 1)) * w;
  const y = (v: number) => h - 3 - ((v - min) / (max - min || 1)) * (h - 6);
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg width={w} height={h} className="spark" aria-hidden="true">
      <path d={d} fill="none" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(values.length - 1)} cy={y(values[values.length - 1])} r={2.75} />
    </svg>
  );
}

/** Change against the period before: green when it moved the good way. */
export function Delta({ now, before, period = 'prior 30 days' }: { now: number; before: number; period?: string }) {
  if (!before && !now) return <span className="delta">No change</span>;
  if (!before) return <span className="delta">New vs {period}</span>;
  const pct = Math.round(((now - before) / before) * 100);
  return <span className="delta"><b className={pct > 0 ? 'up' : pct < 0 ? 'down' : ''}>{pct > 0 ? '↑' : pct < 0 ? '↓' : ''}{Math.abs(pct)}%</b> vs {period}</span>;
}

export function Stat({ label, value, delta, foot, trend, to }: { label: string; value: ReactNode; delta?: ReactNode; foot?: ReactNode; trend?: number[]; to?: string }) {
  const body = (
    <>
      <span className="stat-label">{label}</span>
      <span className="stat-row"><span className="stat-v">{value}</span>{trend && <Sparkline values={trend} />}</span>
      <span className="stat-foot">{delta ?? foot}</span>
    </>
  );
  return to ? <NavLink to={to} className="stat">{body}</NavLink> : <div className="stat">{body}</div>;
}

// ---------------------------------------------------------------- tables that page
export const PAGE_SIZE = 50;

export function Pager({ offset, total, onPage, shown }: { offset: number; total: number; shown: number; onPage: (offset: number) => void }) {
  if (!total) return null;
  return (
    <div className="pager">
      <span className="muted">{total ? `${(offset + 1).toLocaleString()}–${(offset + shown).toLocaleString()} of ${total.toLocaleString()}` : ''}</span>
      <span className="row" style={{ gap: '.25rem' }}>
        <button type="button" className="btn btn-sm btn-quiet" aria-label="Previous page" disabled={offset === 0} onClick={() => onPage(Math.max(0, offset - PAGE_SIZE))}><ChevronLeft size={16} /></button>
        <button type="button" className="btn btn-sm btn-quiet" aria-label="Next page" disabled={offset + shown >= total} onClick={() => onPage(offset + PAGE_SIZE)}><ChevronRight size={16} /></button>
      </span>
    </div>
  );
}

/** A table card: toolbar on top, the table scrolls inside with its header fixed, paging at the bottom. */
export function TableCard({ toolbar, children, pager, className = '' }: { toolbar?: ReactNode; children: ReactNode; pager?: ReactNode; className?: string }) {
  return (
    <section className={`acard tcard ${className}`}>
      {toolbar && <div className="tbar">{toolbar}</div>}
      <div className="tscroll">{children}</div>
      {pager}
    </section>
  );
}

// ---------------------------------------------------------------- sheet (detail over a list)
export function Sheet({ title, sub, actions, onClose, children }: { title: ReactNode; sub?: ReactNode; actions?: ReactNode; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    addEventListener('keydown', k);
    return () => removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="sheet-wrap" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="sheet" role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : 'Details'}>
        <header className="sheet-head">
          <div className="min0">
            <h2>{title}</h2>
            {sub && <div className="sheet-sub">{sub}</div>}
          </div>
          <div className="row" style={{ gap: '.35rem', flexWrap: 'nowrap' }}>
            {actions}
            <button type="button" className="btn btn-sm btn-quiet" onClick={onClose} aria-label="Close"><X size={18} /></button>
          </div>
        </header>
        <div className="sheet-body">{children}</div>
      </aside>
    </div>
  );
}

/** Small key/value grid used in sheets. */
export function Facts({ items }: { items: [string, ReactNode][] }) {
  return <dl className="facts">{items.filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>;
}

/** Keeps a list page's query string when its sheet closes. */
export function useBack(base: string) {
  const loc = useLocation();
  return `${base}${loc.search}`;
}
